import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { OrderingOperations, OrderingSelectors, OrderingUtils } from 'polygon-ordering';
import RedcatPaySavedCardPaymentForm from '../shared/redcatpayments/components/RedcatPaySavedCardPaymentForm';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { getRedcatPaySaleFailureMessage } from '../utils/errors';
import history from '../history';
import { GIFT_CARD_TOP_UP_FIELDS_ROUTE } from '../constants/loyaltyRoutes';

const { getMember } = OrderingSelectors;
const { dollarsToCents } = OrderingUtils;
const { finaliseLoyaltyOrder } = OrderingOperations;

interface GiftCardTopUpFormValues {
  MemberNo: string;
  VerifyPIN: string;
  Amount: string;
}

const GiftCardTopUpRedcatPay = () => {
  const { t } = useTranslation();
  const { values, resetForm, validateForm } = useFormikContext<GiftCardTopUpFormValues>();
  const dispatch = useAppDispatch();
  const member = useAppSelector(getMember);

  const amount = dollarsToCents(parseFloat(values.Amount) || 0);

  const loyalty = {
    loyalty_purchase_type: 'giftcard_topup',
    gc_member_no: values.MemberNo,
  };

  const finaliseTopUp = async (reference: string) => {
    try {
      await dispatch(finaliseLoyaltyOrder(reference)).unwrap();
      enqueueSuccessSnackbar('Gift card top-up successful');
      resetForm();
      // resetForm() clears state.errors directly without re-running validation, so
      // isValid would otherwise stay true against the now-empty fields until the
      // next change/blur/submit. validateForm() without args reads from a stale
      // closure over state.values (the just-paid amount, still valid) because it's
      // called synchronously before the reset's state update lands, so we pass the
      // reset-to values explicitly instead.
      await validateForm({ MemberNo: '', VerifyPIN: '', Amount: '' });
      history.push(GIFT_CARD_TOP_UP_FIELDS_ROUTE);
    } catch (err: any) {
      const failureMessage = getRedcatPaySaleFailureMessage(err, {
        refundFailedMessage: t('refundFailedMessage'),
        fallbackMessage: t('giftCardTopUpFailedMessage'),
      });
      enqueueErrorSnackbar(failureMessage);
    }
  };

  return (
    <RedcatPaySavedCardPaymentForm
      amount={amount}
      loyalty={loyalty}
      receiptEmail={member?.email ?? undefined}
      buttonLabel={t('button.topupButton')}
      failureMessage={t('giftCardTopUpFailedMessage')}
      onFinalise={finaliseTopUp}
    />
  );
};

export default GiftCardTopUpRedcatPay;
